<template>
  <div class="component-testeditorproductselection">
    <v-card flat>
      <v-card-text>
        <!-- Lista delle fasi di lavorazione -->
        <div v-if="displayedPhases.length === 0" class="text-center py-6">
          <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-cog-transfer</v-icon>
          <h4 class="text-h6 mb-2">{{ $t('test.editor.productSelection.noPhases') }}</h4>
          <p class="text--secondary mb-4">
            {{ $t('test.editor.productSelection.noPhasesDescription') }}
          </p>
          <v-btn
            v-if="isEditMode"
            color="primary"
            @click="showAddPhaseDialog = true"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t('test.editor.productSelection.addFirstPhase') }}
          </v-btn>
        </div>

        <!-- Stepper verticale: una fase = uno step, contenuto = griglia scelte -->
        <v-stepper
          v-else
          vertical
          class="elevation-0"
          v-model="currentStep"
        >
          <div v-for="(phase, phaseIndex) in displayedPhases" :key="phase.id">
            <v-stepper-step
              :step="phaseIndex + 1"
              :complete="isEditMode ? true : isPhaseComplete(phase)"
              :editable="isEditMode"
              edit-icon="mdi-pencil"
              :color="phaseColor(phase)"
              :class="{
                'phase-selectable': isEditMode,
                'phase-selected': isEditMode && selectedPhaseIndex === phaseIndex,
                'phase-hidden': isEditMode && phase.visibility === 'always_hidden'
              }"
              @click="onPhaseStepClick(phaseIndex)"
            >
              <div class="phase-header">
                <div class="phase-title-wrapper">
                  <div class="phase-title-block">
                    <span
                      class="phase-title"
                      :class="{ 'phase-hidden': isEditMode && phase.visibility === 'always_hidden' }"
                    >
                      {{ phase.title }}
                    </span>
                    <span v-if="phase.subtitle" class="phase-subtitle text--secondary">{{ phase.subtitle }}</span>
                    <span
                      v-if="isPreviewMode && isPhaseComplete(phase) && getSelectedChoiceForPhase(phase)"
                      class="phase-selection-summary"
                    >
                      <v-icon x-small color="success" class="mr-1">mdi-check-circle</v-icon>
                      {{ getSelectedChoiceForPhase(phase).title || $t('test.editor.productSelection.selectedChoiceFallback') }}
                    </span>
                  </div>
                  <v-tooltip
                    v-if="isEditMode && hasConditions(phase)"
                    bottom
                    :open-delay="200"
                    :close-delay="0"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        :color="getConditionColor(phase)"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        class="condition-warning-btn ml-2 mr-2"
                      >
                        <v-icon>{{ getConditionIcon(phase) }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ getConditionTooltip(phase) }}</span>
                  </v-tooltip>
                </div>
                <div class="phase-step-actions">
                  <v-tooltip
                    v-if="hasProcedure(phase)"
                    bottom
                    :open-delay="200"
                    :close-delay="0"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        color="info"
                        class="phase-action-btn"
                      >
                        <v-icon>mdi-script-text-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('test.editor.productSelection.hasProcedureTooltip') }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="hasChoices(phase)"
                    bottom
                    :open-delay="200"
                    :close-delay="0"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        color="info"
                        class="phase-action-btn"
                      >
                        <v-icon>mdi-format-list-text</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('test.editor.productSelection.hasChoicesTooltip') }}</span>
                  </v-tooltip>
                  <v-chip
                    v-if="isPreviewMode && isPhaseComplete(phase) && getSelectedChoiceProcedure(phase)"
                    x-small color="info" outlined class="ml-1"
                  >
                    <v-icon x-small left>mdi-script-text-outline</v-icon>
                    {{ $t('test.editor.productSelection.procedureChip') }}
                  </v-chip>
                  <template v-if="isEditMode">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          :disabled="phaseIndex === 0"
                          @click.stop="movePhaseUp(phaseIndex)"
                          class="phase-action-btn"
                        >
                          <v-icon small>mdi-chevron-up</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('test.editor.productSelection.moveUp') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          :disabled="phaseIndex === localConfig.phases.length - 1"
                          @click.stop="movePhaseDown(phaseIndex)"
                          class="phase-action-btn"
                        >
                          <v-icon small>mdi-chevron-down</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('test.editor.productSelection.moveDown') }}</span>
                    </v-tooltip>
                    <v-btn
                      icon
                      small
                      color="error"
                      outlined
                      @click.stop="removePhase(phaseIndex)"
                      class="phase-action-btn delete-btn"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </div>
              </div>
            </v-stepper-step>

            <v-stepper-content :step="phaseIndex + 1">
              <v-card class="mb-4" elevation="0">
                <v-card-text>
                  <draggable
                    :list="getPhaseChoicesList(phase)"
                    handle=".sortHandle"
                    draggable=".sortableChoice"
                    :disabled="!isEditMode"
                    @end="(e) => onChoicesReorderForPhase(phaseIndex, e)"
                    tag="div"
                    class="draggable-buttons-container choices-buttons-container"
                  >
                    <v-col
                      v-for="(choice, choiceIndex) in visibleChoices(phase)"
                      :key="choice.id"
                      :cols="getChoiceCols(phase)"
                      class="sortableChoice"
                      :class="{
                        'choice-selectable': isEditMode,
                        'choice-selected': isEditMode && selectedPhaseIndex === phaseIndex && selectedChoiceIndex === choiceIndex,
                        'choice-preview-clickable': isPreviewMode
                      }"
                      @click="onChoiceClickForPhase(phaseIndex, choiceIndex, $event)"
                    >
                      <div class="choice-wrapper">
                        <v-card
                          outlined
                          class="choice-card"
                          :class="{
                            'choice-hidden': isEditMode && choice.visibility === 'always_hidden',
                            'choice-preview-selected': isPreviewMode && isChoiceSelectedForPhase(phase, choice),
                            'choice-card--edit': isEditMode
                          }"
                        >
                          <div class="choice-card-inner">
                            <!-- Colonna azioni a sinistra (dentro la card, solo edit) -->
                            <div v-if="isEditMode" class="choice-actions-strip">
                              <v-btn
                                icon
                                small
                                class="sortHandle"
                                style="cursor: move"
                              >
                                <v-icon small>mdi-drag</v-icon>
                              </v-btn>

                              <div class="choice-actions-middle">
                                <v-tooltip
                                  v-if="hasConditions(choice)"
                                  bottom
                                  :open-delay="200"
                                  :close-delay="0"
                                >
                                  <template #activator="{ on, attrs }">
                                    <v-btn
                                      :color="getConditionColor(choice)"
                                      icon
                                      x-small
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      <v-icon small>{{ getConditionIcon(choice) }}</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>{{ getConditionTooltip(choice) }}</span>
                                </v-tooltip>

                                <v-tooltip
                                  v-if="isChoiceRecommended(choice)"
                                  bottom
                                  :open-delay="200"
                                  :close-delay="0"
                                >
                                  <template #activator="{ on, attrs }">
                                    <v-icon
                                      v-bind="attrs"
                                      v-on="on"
                                      color="amber darken-2"
                                      small
                                    >mdi-star</v-icon>
                                  </template>
                                  <span>{{ $t('test.editor.productSelection.recommendedTooltip') }}</span>
                                </v-tooltip>
                              </div>

                              <v-btn
                                color="error"
                                icon
                                x-small
                                outlined
                                @click.stop="removeChoiceForPhase(phaseIndex, choiceIndex)"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>

                            <!-- Contenuto scelta -->
                            <div class="choice-card-content">
                              <div class="choice-card-header">
                                <span class="choice-card-title">{{ choice.title || $t('test.editor.productSelection.choiceFallbackTitle', { index: choiceIndex + 1 }) }}</span>
                                <v-tooltip v-if="isPreviewMode && isChoiceRecommended(choice)" bottom :open-delay="200" :close-delay="0">
                                  <template #activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on" color="amber darken-2" class="choice-star">mdi-star</v-icon>
                                  </template>
                                  <span>{{ $t('test.editor.productSelection.recommendedTooltip') }}</span>
                                </v-tooltip>
                              </div>
                              <div v-if="hasProcedure(choice)" class="choice-procedure-preview" v-html="getProcedureHtml(choice)"></div>
                              <v-list v-if="choice.products && choice.products.length > 0" dense class="pa-0 choice-products-list">
                                <ProductListItem
                                  v-for="productId in choice.products"
                                  :key="productId"
                                  :product-id="productId"
                                  :disabled="isPreviewMode && isProductDisabledForPhase(phase, productId)"
                                  dense
                                  wrap-title
                                >
                                  <template #actions>
                                    <v-tooltip bottom>
                                      <template v-slot:activator="{ on }">
                                        <v-btn
                                          v-on="on"
                                          fab x-small depressed
                                          color="secondary" dark
                                          @click.stop="onProductDetails(productId)"
                                        >
                                          <v-icon dark small>mdi-eye</v-icon>
                                        </v-btn>
                                      </template>
                                      <span>{{ $t('quotations.manualSelection.tooltips.showDetails') }}</span>
                                    </v-tooltip>
                                  </template>
                                </ProductListItem>
                              </v-list>
                            </div>
                          </div>
                        </v-card>
                      </div>
                    </v-col>
                    <v-col
                      v-if="isEditMode"
                      slot="footer"
                      :cols="getChoiceCols(phase)"
                      class="add-choice-col"
                    >
                      <div class="choice-wrapper">
                        <v-btn
                          block
                          x-large
                          class="bigbutton add-choice-btn"
                          @click="onAddChoiceClick(phaseIndex)"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          {{ $t('test.editor.productSelection.newChoice') }}
                        </v-btn>
                      </div>
                    </v-col>
                  </draggable>

                  <!-- Preview: procedura della fase -->
                  <v-alert
                    v-if="isPreviewMode && hasProcedure(phase)"
                    type="info"
                    text dense border="left"
                    class="mt-3 mb-0"
                  >
                    <div class="body-2" v-html="getProcedureHtml(phase)"></div>
                  </v-alert>

                  <!-- Preview: pulsante "Continua" -->
                  <div v-if="isPreviewMode" class="d-flex justify-end mt-3">
                    <v-btn
                      depressed
                      color="success"
                      :disabled="isPhaseComplete(phase) || (visibleChoices(phase).length > 0 && !getSelectedChoiceForPhase(phase))"
                      @click="handlePhaseContinue(phaseIndex)"
                    >
                      {{ $t('test.editor.productSelection.continueButton') }}
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-stepper-content>
          </div>

          <!-- Step Nuova Fase (solo edit) -->
          <div v-if="isEditMode">
            <v-stepper-step
              :step="localConfig.phases.length + 1"
              :complete="true"
              :editable="true"
              edit-icon="mdi-plus"
              color="success"
              class="add-phase-step"
              @click="showAddPhaseDialog = true"
            >
              <div class="phase-header">
                <span class="phase-title add-phase-title">
                  {{ $t('test.editor.productSelection.addNewPhase') }}
                </span>
              </div>
            </v-stepper-step>
          </div>

          <!-- Preview: bottoni navigazione -->
          <v-card-actions v-if="isPreviewMode">
            <v-btn text color="orange" @click="onRestart()">
              {{ $t('test.editor.stage.restart') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn depressed color="success" @click="onContinue()" :disabled="!canContinue">
              {{ $t('test.editor.productSelection.completeButton') }}
            </v-btn>
          </v-card-actions>
        </v-stepper>
      </v-card-text>
    </v-card>

    <!-- Dialog per aggiungere una nuova fase -->
    <v-dialog v-model="showAddPhaseDialog" max-width="500" @input="onAddPhaseDialogInput">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-plus</v-icon>
          {{ $t('test.editor.productSelection.addPhaseDialog.title') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="addPhaseForm" v-model="addPhaseFormValid">
            <v-text-field
              ref="addPhaseTitleField"
              v-model="newPhaseTitle"
              :label="$t('test.editor.productSelection.addPhaseDialog.nameLabel')"
              :placeholder="$t('test.editor.productSelection.addPhaseDialog.namePlaceholder')"
              outlined
              dense
              :rules="phaseTitleRules"
              @keyup.enter="addPhase"
            ></v-text-field>

            <v-text-field
              v-model="newPhaseSubtitle"
              :label="$t('test.editor.productSelection.addPhaseDialog.subtitleLabel')"
              :placeholder="$t('test.editor.productSelection.addPhaseDialog.subtitlePlaceholder')"
              outlined
              dense
              class="mt-3"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text @click="cancelAddPhase">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!addPhaseFormValid"
            @click="addPhase"
          >
            {{ $t('test.editor.dialogs.shared.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog per aggiungere una nuova scelta -->
    <v-dialog v-model="showAddChoiceDialog" max-width="500" @input="onAddChoiceDialogInput">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-plus</v-icon>
          {{ $t('test.editor.productSelection.addChoiceDialog.title') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="addChoiceForm" v-model="addChoiceFormValid" @submit.prevent="addChoice">
            <v-text-field
              ref="addChoiceTitleField"
              v-model="newChoiceTitle"
              :label="$t('test.editor.productSelection.addChoiceDialog.nameLabel')"
              :placeholder="$t('test.editor.productSelection.addChoiceDialog.namePlaceholder')"
              outlined
              dense
              :rules="choiceTitleRules"
              @keyup.enter="addChoice"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text @click="cancelAddChoice">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="addChoice"
          >
            {{ $t('test.editor.dialogs.shared.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog di conferma per rimozione fase -->
    <v-dialog v-model="showRemovePhaseDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-delete</v-icon>
          {{ $t('test.editor.productSelection.removePhaseDialog.title') }}
        </v-card-title>

        <v-card-text>
          <v-alert
            type="warning"
            text
            class="mb-4"
          >
            <strong>{{ $t('test.editor.dialogs.shared.warningPrefix') }}</strong> {{ pendingRemovePhase ? $t('test.editor.productSelection.removePhaseDialog.body', { phaseName: pendingRemovePhase.title }) : '' }}
          </v-alert>

          <p>
            <strong>{{ $t('test.editor.dialogs.shared.confirmProceed') }}</strong>
            {{ $t('test.editor.dialogs.shared.irreversible') }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text @click="showRemovePhaseDialog = false">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="error" text @click="confirmRemovePhase">
            {{ $t('test.editor.dialogs.shared.remove') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog dettagli prodotto -->
    <ProductDetailsDialog
      v-model="productDetailsDialog"
      :product="productDetailsProduct"
      :select="false"
      :unselect="false"
    />

    <!-- Dialog di conferma per rimozione scelta -->
    <v-dialog v-model="showRemoveChoiceDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-delete</v-icon>
          {{ $t('test.editor.productSelection.removeChoiceDialog.title') }}
        </v-card-title>

        <v-card-text>
          <v-alert
            type="warning"
            text
            class="mb-4"
          >
            <strong>{{ $t('test.editor.dialogs.shared.warningPrefix') }}</strong> {{ pendingRemoveChoice ? $t('test.editor.productSelection.removeChoiceDialog.body', { choiceTitle: pendingRemoveChoice.title || $t('test.editor.productSelection.removeChoiceDialog.unnamedFallback') }) : '' }}
          </v-alert>

          <p>
            <strong>{{ $t('test.editor.dialogs.shared.confirmProceed') }}</strong>
            {{ $t('test.editor.dialogs.shared.irreversible') }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text @click="showRemoveChoiceDialog = false">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="error" text @click="confirmRemoveChoice">
            {{ $t('test.editor.dialogs.shared.remove') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import ProductListItem from '@/components/ProductListItem';
import ProductDetailsDialog from '@/components/ProductDetailsDialog';
import { evaluateConditions as evaluateConditionsUtil, isItemVisible } from '@/utils/wizard-conditions';
import {
  buildPreviouslySelectedProductIds,
  buildPriorPhaseProductCatalog,
  filterChoicesByCrossPhaseProducts,
} from '@/utils/wizard-cross-phase';

export default {
  name: 'TestEditorProductSelection',

  components: {
    draggable,
    ProductListItem,
    ProductDetailsDialog,
  },

  props: {
    // Modalità di visualizzazione (edit/preview)
    mode: {
      type: String,
      default: 'edit',
      validator: (value) => ['edit', 'preview'].includes(value),
    },

    // Configurazione del test (per accesso ai dati degli stage precedenti)
    test: {
      type: Array,
      default: () => [],
    },

    // Stato della preview (v-model, per simulare le risposte)
    value: {
      type: Object,
      default: () => ({}),
    },

    // Elemento selezionato per la configurazione
    selectedItem: {
      type: Object,
      default: null,
    },

    selectedItemType: {
      type: String,
      default: null,
    },

    // Fasi di lavorazione dalla configurazione del test
    phases: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    // Stato preview (v-model sync)
    status: null,

    // Dati locali del componente
    localConfig: {
      phases: [],
      choices: {},
    },

    // Gestione delle fasi
    currentStep: 1,
    selectedPhaseIndex: null,
    showAddPhaseDialog: false,
    showRemovePhaseDialog: false,
    pendingRemovePhase: null,
    newPhaseTitle: '',
    newPhaseSubtitle: '',
    addPhaseFormValid: false,

    // Gestione delle scelte
    showAddChoiceDialog: false,
    showRemoveChoiceDialog: false,
    pendingRemoveChoice: null,
    pendingRemovePhaseIndex: null,
    addChoicePhaseIndex: null, // indice fase quando si apre "Aggiungi Scelta" (fallback se selectedPhase diventa null)
    newChoiceTitle: '',
    addChoiceFormValid: false,
    selectedChoiceIndex: null,

    // Dettagli prodotto
    productDetailsDialog: false,
    productDetailsProduct: null,

  }),

  computed: {
    allStageNames() {
      return ['stage1', 'stage2', 'stage3', 'stage4'];
    },

    // Computed properties per la logica del componente
    isEditMode() {
      return this.mode === 'edit';
    },

    isPreviewMode() {
      return this.mode === 'preview';
    },

    selectedPhase() {
      if (this.selectedPhaseIndex !== null && this.localConfig.phases[this.selectedPhaseIndex]) {
        return this.localConfig.phases[this.selectedPhaseIndex];
      }
      return null;
    },

    selectedChoice() {
      if (this.selectedPhase && this.selectedChoiceIndex !== null && this.selectedPhase.choices && this.selectedPhase.choices[this.selectedChoiceIndex]) {
        return this.selectedPhase.choices[this.selectedChoiceIndex];
      }
      return null;
    },

    phaseChoicesList() {
      if (!this.selectedPhase) return [];
      if (!Array.isArray(this.selectedPhase.choices)) {
        this.$set(this.selectedPhase, 'choices', []);
      }
      return this.selectedPhase.choices;
    },

    userLanguage() {
      return this.$i18n.locale || 'it';
    },

    // Preview: fasi visibili (filtrate per condizioni)
    visiblePhases() {
      if (!this.localConfig.phases) return [];
      if (this.isEditMode) return this.localConfig.phases;
      return this.localConfig.phases.filter((phase) => this.isPhaseVisible(phase));
    },

    // Fasi da mostrare nel template (edit: tutte, preview: filtrate)
    displayedPhases() {
      return this.isEditMode ? this.localConfig.phases : this.visiblePhases;
    },

    // Preview: tutte le fasi visibili sono completate
    isStageComplete() {
      if (this.isEditMode) return false;
      return this.visiblePhases.length > 0
        && this.visiblePhases.every((phase) => this.isPhaseComplete(phase));
    },

    // Preview: pulsante "Completa" abilitato
    canContinue() {
      return this.isStageComplete;
    },

    // Cross-phase: per ogni fase, Set di product ID selezionati in tutte le fasi precedenti
    previouslySelectedProductIds() {
      if (!this.status || !this.status.stages || !this.status.stages.stage4
        || !this.status.stages.stage4.phases) {
        return {};
      }
      return buildPreviouslySelectedProductIds(
        this.visiblePhases,
        this.status.stages.stage4.phases,
      );
    },

    // Cross-phase: per ogni fase, Set di TUTTI i product ID presenti in qualsiasi scelta delle fasi precedenti
    priorPhaseProductCatalog() {
      return buildPriorPhaseProductCatalog(this.visiblePhases);
    },

    // Regole di validazione (computed per supporto i18n)
    phaseTitleRules() {
      return [
        (v) => !!v || this.$t('test.editor.validation.phaseTitleRequired'),
        (v) => (v && v.length >= 3) || this.$t('test.editor.validation.phaseTitleMinLength'),
        (v) => (v && v.length <= 100) || this.$t('test.editor.validation.phaseTitleMaxLength'),
      ];
    },

    choiceTitleRules() {
      return [
        (v) => !v || (v.length <= 100) || this.$t('test.editor.validation.choiceTitleMaxLength'),
      ];
    },
  },

  created() {
    this.status = this.value;
  },

  watch: {
    // Sync v-model preview status
    value: {
      handler(newValue) {
        this.status = newValue;
      },
      deep: true,
    },

    // Preview: quando si entra in preview, naviga alla prima fase non completata
    mode(newMode) {
      if (newMode === 'preview') {
        this.$nextTick(() => {
          const idx = this.visiblePhases.findIndex(
            (p) => !this.isPhaseComplete(p),
          );
          if (idx !== -1) this.currentStep = idx + 1;
        });
      }
    },

    // Watchers per reagire ai cambiamenti
    test: {
      handler(newTest) {
        this.initializeFromTest(newTest);
      },
      deep: true,
      immediate: true,
    },

    phases: {
      handler(newPhases) {
        this.syncPhasesFromTest(newPhases);
      },
      deep: true,
      immediate: true,
    },

    showAddPhaseDialog(isOpen) {
      if (isOpen) {
        this.newPhaseTitle = '';
        this.newPhaseSubtitle = '';
        this.addPhaseFormValid = false;
        this.$nextTick(() => {
          if (this.$refs.addPhaseForm) {
            this.$refs.addPhaseForm.reset();
          }
          setTimeout(() => this.focusAddPhaseTitle(), 50);
        });
      }
    },

    showAddChoiceDialog(isOpen) {
      if (isOpen) {
        this.newChoiceTitle = '';
        this.addChoiceFormValid = false;
        this.$nextTick(() => {
          if (this.$refs.addChoiceForm) {
            this.$refs.addChoiceForm.reset();
          }
          setTimeout(() => this.focusAddChoiceTitle(), 50);
        });
      }
    },
  },

  methods: {
    // Metodi del componente

    /**
     * Inizializza la configurazione basandosi sui dati del test
     */
    initializeFromTest(test) {
      // eslint-disable-next-line no-console
      console.log('TestEditorProductSelection: initializeFromTest called with:', test);
      // La sincronizzazione delle fasi è gestita dal watcher delle phases
    },

    onAddPhaseDialogInput(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          setTimeout(() => this.focusAddPhaseTitle(), 50);
        });
      }
    },

    focusAddPhaseTitle() {
      const field = this.$refs.addPhaseTitleField;
      if (field && typeof field.focus === 'function') {
        field.focus();
        return;
      }
      const input = field && field.$el && field.$el.querySelector('input');
      if (input) {
        input.focus();
      }
    },

    onAddChoiceDialogInput(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          setTimeout(() => this.focusAddChoiceTitle(), 50);
        });
      }
    },

    focusAddChoiceTitle() {
      const field = this.$refs.addChoiceTitleField;
      if (field && typeof field.focus === 'function') {
        field.focus();
        return;
      }
      const input = field && field.$el && field.$el.querySelector('input');
      if (input) {
        input.focus();
      }
    },

    /**
     * Sincronizza le fasi dalla configurazione del test
     */
    syncPhasesFromTest(phases) {
      // eslint-disable-next-line no-console
      console.log('TestEditorProductSelection: syncPhasesFromTest called with:', phases);
      this.localConfig.phases = phases || [];
      this.currentStep = Math.min(this.currentStep, Math.max(1, this.localConfig.phases.length || 1));
    },

    /**
     * Gestisce l'aggiornamento della configurazione
     */
    onConfigUpdate() {
      // eslint-disable-next-line no-console
      console.log('TestEditorProductSelection: onConfigUpdate called');
      // Emetti solo le fasi per l'aggiornamento della configurazione del test
      this.$emit('config-updated', {
        phases: this.localConfig.phases,
      });
    },

    /**
     * Gestisce la selezione di un elemento
     */
    onElementSelected(elementType, elementData) {
      // eslint-disable-next-line no-console
      console.log('TestEditorProductSelection: onElementSelected called with:', elementType, elementData);
      this.$emit('element-selected', elementType, elementData);
    },

    // Metodi per la gestione delle fasi

    /**
     * Gestisce la selezione di una fase (click su step)
     */
    onPhaseStepClick(phaseIndex) {
      if (this.isEditMode) {
        this.currentStep = phaseIndex + 1;
        this.selectedPhaseIndex = phaseIndex;
        this.selectedChoiceIndex = null;
        const phase = this.localConfig.phases[phaseIndex];
        if (phase && !Array.isArray(phase.choices)) {
          this.$set(phase, 'choices', []);
        }
        this.onElementSelected('phase', phase);
      } else {
        this.currentStep = phaseIndex + 1;
      }
    },

    /**
     * Mantiene compatibilità: selezione fase da padre (es. dopo add)
     */
    onPhaseSelected(phase, index) {
      if (this.isEditMode) {
        this.currentStep = index + 1;
        this.selectedPhaseIndex = index;
        this.selectedChoiceIndex = null;
        if (phase && !Array.isArray(phase.choices)) {
          this.$set(phase, 'choices', []);
        }
        this.onElementSelected('phase', phase);
      }
    },

    /**
     * Colore step in base a presenza scelte (come questionColor in Stage)
     */
    phaseColor(phase) {
      // In edit: sempre primary (solo la fase selezionata ha icona blu via CSS .phase-selected)
      if (this.isEditMode) return 'primary';
      // In preview: success se la fase è completata
      return this.isPhaseComplete(phase) ? 'success' : 'primary';
    },

    /**
     * Restituisce l'array choices della fase, inizializzandolo se assente (per draggable)
     */
    getPhaseChoicesList(phase) {
      if (!phase) return [];
      if (!Array.isArray(phase.choices)) {
        this.$set(phase, 'choices', []);
      }
      return phase.choices;
    },

    /**
     * Click su una scelta nella griglia (con indice fase)
     */
    onChoiceClickForPhase(phaseIndex, choiceIndex, event) {
      if (this.isPreviewMode) {
        this.handleChoiceSelection(phaseIndex, choiceIndex);
        return;
      }
      if (!this.isEditMode) return;
      if (event && (event.target.closest('.sortHandle') || event.target.closest('.v-btn[color="error"]'))) return;
      this.selectedPhaseIndex = phaseIndex;
      this.selectedChoiceIndex = choiceIndex;
      const phase = this.localConfig.phases[phaseIndex];
      const choice = phase && this.visibleChoices(phase)[choiceIndex];
      if (choice) this.onElementSelected('choice', choice);
    },

    onChoiceClick(index, event) {
      if (!this.isEditMode) return;
      if (event && (event.target.closest('.sortHandle') || event.target.closest('.v-btn[color="error"]'))) return;
      this.onChoiceSelected(this.visibleChoices(this.selectedPhase)[index], index);
    },

    /**
     * Apre dialog rimozione scelta per la fase indicata
     */
    removeChoiceForPhase(phaseIndex, choiceIndex) {
      const phase = this.localConfig.phases[phaseIndex];
      if (!phase || !phase.choices || !phase.choices[choiceIndex]) return;
      this.pendingRemovePhaseIndex = phaseIndex;
      this.pendingRemoveChoice = phase.choices[choiceIndex];
      this.showRemoveChoiceDialog = true;
    },

    /**
     * Riordino scelte per una fase (dopo drag)
     */
    onChoicesReorderForPhase(phaseIndex, event) {
      if (this.selectedPhaseIndex === phaseIndex && this.selectedChoiceIndex !== null) {
        const { oldIndex, newIndex } = event;
        if (this.selectedChoiceIndex === oldIndex) {
          this.selectedChoiceIndex = newIndex;
        } else if (this.selectedChoiceIndex > oldIndex && this.selectedChoiceIndex <= newIndex) {
          this.selectedChoiceIndex--;
        } else if (this.selectedChoiceIndex < oldIndex && this.selectedChoiceIndex >= newIndex) {
          this.selectedChoiceIndex++;
        }
      }
      this.onConfigUpdate();
    },

    /**
     * Click su "Nuova Scelta" in una fase: seleziona la fase e apre il dialog
     */
    onAddChoiceClick(phaseIndex) {
      this.selectedPhaseIndex = phaseIndex;
      this.addChoicePhaseIndex = phaseIndex;
      this.currentStep = phaseIndex + 1;
      const phase = this.localConfig.phases[phaseIndex];
      if (phase && !Array.isArray(phase.choices)) {
        this.$set(phase, 'choices', []);
      }
      this.showAddChoiceDialog = true;
    },

    movePhaseUp(phaseIndex) {
      if (phaseIndex <= 0) return;
      const { phases } = this.localConfig;
      const phase = phases[phaseIndex];
      phases.splice(phaseIndex, 1);
      phases.splice(phaseIndex - 1, 0, phase);
      if (this.selectedPhaseIndex === phaseIndex) {
        this.selectedPhaseIndex = phaseIndex - 1;
      } else if (this.selectedPhaseIndex === phaseIndex - 1) {
        this.selectedPhaseIndex = phaseIndex;
      }
      this.currentStep = this.selectedPhaseIndex !== null ? this.selectedPhaseIndex + 1 : Math.min(this.currentStep, phases.length);
      this.onConfigUpdate();
    },

    movePhaseDown(phaseIndex) {
      if (phaseIndex >= this.localConfig.phases.length - 1) return;
      const { phases } = this.localConfig;
      const phase = phases[phaseIndex];
      phases.splice(phaseIndex, 1);
      phases.splice(phaseIndex + 1, 0, phase);
      if (this.selectedPhaseIndex === phaseIndex) {
        this.selectedPhaseIndex = phaseIndex + 1;
      } else if (this.selectedPhaseIndex === phaseIndex + 1) {
        this.selectedPhaseIndex = phaseIndex;
      }
      this.currentStep = this.selectedPhaseIndex !== null ? this.selectedPhaseIndex + 1 : Math.min(this.currentStep, phases.length);
      this.onConfigUpdate();
    },

    /**
     * Gestisce la selezione di una scelta
     */
    onChoiceSelected(choice, index) {
      if (this.isEditMode) {
        this.selectedChoiceIndex = index;
        this.onElementSelected('choice', choice);
      }
    },

    /**
     * Aggiunge una nuova fase di lavorazione
     */
    addPhase() {
      const title = (this.newPhaseTitle || '').trim();
      if (!this.addPhaseFormValid || !title) {
        return;
      }

      const newPhase = {
        id: this.generateId(),
        title,
        subtitle: (this.newPhaseSubtitle || '').trim() || null,
        visibility: 'always_visible',
        conditions: [],
        procedure: null,
        choices: [],
      };

      // Aggiungi la fase alla configurazione locale
      this.localConfig.phases.push(newPhase);

      // Emetti l'evento per aggiornare la configurazione del test
      this.onConfigUpdate();

      // Seleziona automaticamente la nuova fase e espandi il suo step
      this.selectedPhaseIndex = this.localConfig.phases.length - 1;
      this.currentStep = this.localConfig.phases.length;
      this.onElementSelected('phase', newPhase);

      // Reset del form
      this.cancelAddPhase();
    },

    /**
     * Rimuove una fase di lavorazione (mostra dialog di conferma)
     */
    removePhase(index) {
      if (index >= 0 && index < this.localConfig.phases.length) {
        // Salva la fase da rimuovere per il dialog di conferma
        this.pendingRemovePhase = this.localConfig.phases[index];
        this.showRemovePhaseDialog = true;
      }
    },

    /**
     * Conferma la rimozione di una fase di lavorazione
     */
    confirmRemovePhase() {
      if (this.pendingRemovePhase) {
        // Trova l'indice della fase da rimuovere
        const index = this.localConfig.phases.findIndex((phase) => phase.id === this.pendingRemovePhase.id);

        if (index >= 0 && index < this.localConfig.phases.length) {
          // Rimuovi la fase dalla configurazione locale
          this.localConfig.phases.splice(index, 1);

          // Emetti l'evento per aggiornare la configurazione del test
          this.onConfigUpdate();

          // Aggiorna currentStep e selezione
          if (this.selectedPhaseIndex === index) {
            this.selectedPhaseIndex = null;
          } else if (this.selectedPhaseIndex > index) {
            this.selectedPhaseIndex--;
          }
          this.currentStep = Math.min(this.currentStep, Math.max(1, this.localConfig.phases.length));
          if (this.localConfig.phases.length === 0) {
            this.currentStep = 1;
          }
        }

        // Reset delle variabili del dialog
        this.pendingRemovePhase = null;
      }

      this.showRemovePhaseDialog = false;
    },

    /**
     * Annulla l'aggiunta di una nuova fase
     */
    cancelAddPhase() {
      this.showAddPhaseDialog = false;
      this.newPhaseTitle = '';
      this.newPhaseSubtitle = '';
      this.addPhaseFormValid = false;

      // Reset del form
      if (this.$refs.addPhaseForm) {
        this.$refs.addPhaseForm.reset();
      }
    },

    /**
     * Gestisce il riordinamento delle fasi tramite drag & drop
     */
    onPhasesReorder(event) {
      // Il riordinamento è già stato applicato automaticamente dal v-model di draggable
      // Aggiorna l'indice di selezione se necessario
      if (this.selectedPhaseIndex !== null) {
        const { oldIndex } = event;
        const { newIndex } = event;

        // Se la fase selezionata è stata spostata, aggiorna l'indice
        if (this.selectedPhaseIndex === oldIndex) {
          this.selectedPhaseIndex = newIndex;
        } else if (this.selectedPhaseIndex > oldIndex && this.selectedPhaseIndex <= newIndex) {
          // Se una fase è stata spostata prima della fase selezionata
          this.selectedPhaseIndex--;
        } else if (this.selectedPhaseIndex < oldIndex && this.selectedPhaseIndex >= newIndex) {
          // Se una fase è stata spostata dopo la fase selezionata
          this.selectedPhaseIndex++;
        }
      }

      // Emetti l'evento di cambio
      this.onConfigUpdate();
    },

    /**
     * Gestisce il riordinamento delle scelte tramite drag & drop
     */
    onChoicesReorder(event) {
      // Il riordinamento è già stato applicato automaticamente dal v-model di draggable
      // Aggiorna l'indice di selezione se necessario
      if (this.selectedChoiceIndex !== null) {
        const { oldIndex } = event;
        const { newIndex } = event;

        // Se la scelta selezionata è stata spostata, aggiorna l'indice
        if (this.selectedChoiceIndex === oldIndex) {
          this.selectedChoiceIndex = newIndex;
        } else if (this.selectedChoiceIndex > oldIndex && this.selectedChoiceIndex <= newIndex) {
          // Se una scelta è stata spostata prima della scelta selezionata
          this.selectedChoiceIndex--;
        } else if (this.selectedChoiceIndex < oldIndex && this.selectedChoiceIndex >= newIndex) {
          // Se una scelta è stata spostata dopo la scelta selezionata
          this.selectedChoiceIndex++;
        }
      }

      // Emetti l'evento di cambio
      this.onConfigUpdate();
    },

    /**
     * Verifica se una fase o scelta ha condizioni configurate
     */
    hasConditions(item) {
      return item && item.conditions && item.conditions.length > 0;
    },

    /**
     * Scelte visibili per la fase (in edit tutte, in preview filtrate per condizioni)
     */
    visibleChoices(phase) {
      if (!phase || !phase.choices) return [];
      if (this.isEditMode) return phase.choices;

      let choices = phase.choices.filter((choice) => this.isChoiceVisible(choice));

      // Cross-phase: filtra scelte con prodotti di fasi precedenti
      choices = filterChoicesByCrossPhaseProducts(
        choices,
        this.previouslySelectedProductIds[phase.id],
        this.priorPhaseProductCatalog[phase.id],
      );

      // Scelte consigliate prima (decorate-sort-undecorate)
      const decorated = choices.map((choice) => ({
        choice,
        priority: this.isChoiceRecommended(choice) ? 0 : 1,
      }));
      decorated.sort((a, b) => a.priority - b.priority);
      choices = decorated.map((item) => item.choice);

      return choices;
    },

    /**
     * Colonne per la griglia scelte: 2 colonne (6 cols per cella su griglia 12)
     */
    getChoiceCols(phase) {
      return 6;
    },

    /**
     * Restituisce true se la fase ha almeno una scelta (valore derivato da phase.choices, non persistito).
     */
    hasChoices(phase) {
      return phase && phase.choices && phase.choices.length > 0;
    },

    /**
     * Verifica se un prodotto è disabilitato per una fase (già selezionato in una fase precedente)
     */
    isProductDisabledForPhase(phase, productId) {
      const selected = this.previouslySelectedProductIds[phase.id];
      return selected ? selected.has(productId) : false;
    },

    // --- Preview: valutazione condizioni ---

    /**
     * Determina se una fase è visibile in preview (stessa logica di isQuestionVisible in TestEditorStage)
     */
    isPhaseVisible(phase) {
      if (this.isEditMode) return true;
      return isItemVisible(phase, this.status.stages, this.allStageNames);
    },

    isChoiceVisible(choice) {
      if (this.isEditMode) return true;
      return isItemVisible(choice, this.status.stages, this.allStageNames);
    },

    evaluateConditions(conditions) {
      return evaluateConditionsUtil(conditions, this.status.stages, this.allStageNames);
    },

    // --- Preview: completamento fase ---

    /**
     * Verifica se una fase è completata in preview.
     */
    isPhaseComplete(phase) {
      if (this.isEditMode) return false;
      if (!this.status || !this.status.stages || !this.status.stages.stage4 || !phase.id) {
        return false;
      }

      const phaseResponse = this.status.stages.stage4.phases
        ? this.status.stages.stage4.phases[phase.id]
        : null;
      return phaseResponse && phaseResponse.complete;
    },

    /**
     * Verifica se una scelta è quella selezionata per la fase in preview
     */
    isChoiceSelectedForPhase(phase, choice) {
      if (!this.status || !this.status.stages || !this.status.stages.stage4
        || !this.status.stages.stage4.phases || !phase.id) {
        return false;
      }
      const phaseResponse = this.status.stages.stage4.phases[phase.id];
      return phaseResponse && phaseResponse.choiceId === choice.id;
    },

    // --- Preview: dati scelta selezionata ---

    /**
     * Restituisce l'oggetto choice selezionato per la fase, o null
     */
    getSelectedChoiceForPhase(phase) {
      if (!this.status || !this.status.stages || !this.status.stages.stage4
        || !this.status.stages.stage4.phases || !this.status.stages.stage4.phases[phase.id]) {
        return null;
      }
      const { choiceId } = this.status.stages.stage4.phases[phase.id];
      const choices = phase.choices || [];
      return choices.find((c) => c.id === choiceId) || null;
    },

    /**
     * Restituisce la procedura HTML della scelta selezionata (senza troncamento)
     */
    getSelectedChoiceProcedure(phase) {
      const choice = this.getSelectedChoiceForPhase(phase);
      if (!choice || !this.hasProcedure(choice)) return null;
      if (!choice.procedure) return null;
      if (typeof choice.procedure === 'string') return choice.procedure;
      if (typeof choice.procedure === 'object') {
        return choice.procedure[this.userLanguage] || choice.procedure.it || '';
      }
      return null;
    },

    // --- Preview: interazione ---

    /**
     * Gestisce la selezione di una scelta in preview (registra senza completare)
     */
    handleChoiceSelection(phaseIndex, choiceIndex) {
      const phases = this.visiblePhases;
      const phase = phases[phaseIndex];
      if (!phase || !phase.id) return;

      const choices = this.visibleChoices(phase);
      const choice = choices[choiceIndex];
      if (!choice) return;

      // Inizializza la struttura phases se necessario
      if (!this.status.stages.stage4.phases) {
        this.$set(this.status.stages.stage4, 'phases', {});
      }

      // Controlla se la scelta è cambiata rispetto alla selezione precedente
      const previousSelection = this.status.stages.stage4.phases[phase.id];
      const choiceChanged = !previousSelection || previousSelection.choiceId !== choice.id;

      // Registra la selezione senza completare (l'utente deve cliccare "Continua")
      this.$set(this.status.stages.stage4.phases, phase.id, {
        complete: false,
        choiceTitle: choice.title || this.$t('test.editor.productSelection.choiceFallbackTitle', { index: choiceIndex + 1 }),
        choiceId: choice.id,
      });

      // Cross-phase: cancella le selezioni delle fasi successive solo se la scelta è cambiata
      if (choiceChanged) {
        const allPhases = this.localConfig.phases || [];
        const currentIndexInAll = allPhases.findIndex((p) => p.id === phase.id);
        if (currentIndexInAll !== -1) {
          for (let i = currentIndexInAll + 1; i < allPhases.length; i += 1) {
            const laterPhase = allPhases[i];
            if (this.status.stages.stage4.phases[laterPhase.id]) {
              this.$delete(this.status.stages.stage4.phases, laterPhase.id);
            }
          }
        }
      }

      this.$emit('input', this.status);
    },

    /**
     * Completa una fase (click su "Continua")
     */
    handlePhaseContinue(phaseIndex) {
      const phases = this.visiblePhases;
      const phase = phases[phaseIndex];
      if (!phase || !phase.id) return;

      if (!this.status.stages.stage4.phases) {
        this.$set(this.status.stages.stage4, 'phases', {});
      }

      const existing = this.status.stages.stage4.phases[phase.id] || {};
      this.$set(this.status.stages.stage4.phases, phase.id, {
        ...existing,
        complete: true,
      });

      // Auto-avanzamento alla prossima fase incompleta
      this.$nextTick(() => {
        const nextPhaseIndex = phases.findIndex(
          (p, idx) => idx > phaseIndex && !this.isPhaseComplete(p),
        );
        if (nextPhaseIndex !== -1) {
          this.currentStep = nextPhaseIndex + 1;
        } else if (this.isStageComplete) {
          this.currentStep = phases.length + 1;
        }
      });

      this.$emit('input', this.status);
    },

    /**
     * Pulsante "Completa": marca il test come completo (Stage 4 è l'ultimo)
     */
    onContinue() {
      // Se ci sono fasi incomplete, naviga alla prima
      const phases = this.visiblePhases;
      const nextIncompleteIndex = phases.findIndex(
        (phase) => !this.isPhaseComplete(phase),
      );

      if (nextIncompleteIndex !== -1) {
        this.currentStep = nextIncompleteIndex + 1;
        this.$emit('input', this.status);
        return;
      }

      // Tutte le fasi completate: marca stage e test come completi
      this.status.stages.stage4.complete = true;
      this.status.complete = true;
      this.$emit('input', this.status);
    },

    /**
     * Pulsante "Ricomincia": reset completo di tutti gli stage
     */
    onRestart() {
      this.status.currentStep = 1;
      this.status.currentStage = 1;

      const allStages = ['stage1', 'stage2', 'stage3', 'stage4'];
      allStages.forEach((stageName) => {
        if (this.status.stages[stageName]) {
          this.$set(this.status.stages[stageName], 'questions', {});
          if (stageName === 'stage4') {
            this.$set(this.status.stages[stageName], 'phases', {});
          }
          this.status.stages[stageName].complete = false;
        }
      });

      this.status.complete = false;
      this.$emit('input', this.status);
    },

    /**
     * Restituisce true se fase o scelta ha procedura (valore derivato da procedure, non persistito).
     */
    hasProcedure(item) {
      return item && item.procedure != null && String(item.procedure).trim() !== '';
    },

    /**
     * Restituisce l'HTML della procedura di una fase o scelta (supporto multilingua).
     */
    getProcedureHtml(item) {
      if (!item || !item.procedure) return '';
      if (typeof item.procedure === 'string') return item.procedure;
      if (typeof item.procedure === 'object') {
        return item.procedure[this.userLanguage] || item.procedure.it || '';
      }
      return '';
    },

    /**
     * Mockup: scelta consigliata se recommended !== 'never' (nessuna logica condizioni).
     */
    isChoiceRecommended(choice) {
      if (!choice || !choice.recommended || choice.recommended === 'never') return false;
      if (choice.recommended === 'always') return true;
      if (choice.recommended === 'conditions') {
        // In edit mode, mostra la stella se ci sono condizioni configurate (feedback visivo)
        if (this.isEditMode) {
          return choice.recommendedConditions && choice.recommendedConditions.length > 0;
        }
        // In preview mode, valuta le condizioni effettive
        return this.evaluateConditions(choice.recommendedConditions);
      }
      return false;
    },

    /**
     * Restituisce l'icona appropriata per le condizioni di visibilità
     */
    getConditionIcon(item) {
      if (!this.hasConditions(item)) return null;

      if (item.visibility === 'always_visible') {
        return 'mdi-eye-off';
      }
      if (item.visibility === 'always_hidden') {
        return 'mdi-eye';
      }
      return 'mdi-cog';
    },

    /**
     * Restituisce il colore appropriato per le condizioni di visibilità
     */
    getConditionColor(item) {
      if (!this.hasConditions(item)) return 'warning';

      if (item.visibility === 'always_visible') {
        return 'error';
      }
      if (item.visibility === 'always_hidden') {
        return 'success';
      }
      return 'warning';
    },

    /**
     * Genera il tooltip per le condizioni di visibilità
     */
    getConditionTooltip(item) {
      if (!this.hasConditions(item)) return '';

      const conditionCount = item.conditions.length;

      if (item.visibility === 'always_visible') {
        return conditionCount === 1
          ? this.$t('test.editor.config.conditions.tooltipHiddenSingle')
          : this.$t('test.editor.config.conditions.tooltipHiddenMultiple', { count: conditionCount });
      }
      if (item.visibility === 'always_hidden') {
        return conditionCount === 1
          ? this.$t('test.editor.config.conditions.tooltipShownSingle')
          : this.$t('test.editor.config.conditions.tooltipShownMultiple', { count: conditionCount });
      }
      return this.$t('test.editor.config.conditions.tooltipConditionsCount', { count: conditionCount });
    },

    /**
     * Genera un ID univoco per le fasi
     */
    generateId() {
      return `phase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },

    // Metodi per la gestione delle scelte

    /**
     * Aggiunge una nuova scelta alla fase selezionata
     */
    addChoice() {
      const phase = this.selectedPhase || (this.addChoicePhaseIndex != null && this.localConfig.phases[this.addChoicePhaseIndex]) || null;
      if (!phase) {
        return;
      }

      if (!phase.choices) {
        this.$set(phase, 'choices', []);
      }

      const newChoice = {
        id: this.generateChoiceId(),
        title: (this.newChoiceTitle || '').trim() || null,
        visibility: 'always_visible',
        conditions: [],
        recommended: 'never',
        recommendedConditions: [],
        procedure: null,
        withProducts: false,
        products: [],
      };

      phase.choices.push(newChoice);

      this.onConfigUpdate();

      this.selectedPhaseIndex = this.addChoicePhaseIndex != null ? this.addChoicePhaseIndex : this.selectedPhaseIndex;
      this.selectedChoiceIndex = phase.choices.length - 1;
      this.onElementSelected('choice', newChoice);

      this.cancelAddChoice();
    },

    /**
     * Rimuove una scelta dalla fase selezionata (mostra dialog di conferma)
     */
    removeChoice(index) {
      if (!this.selectedPhase || !this.selectedPhase.choices || index < 0 || index >= this.selectedPhase.choices.length) {
        return;
      }
      this.pendingRemovePhaseIndex = this.selectedPhaseIndex;
      this.pendingRemoveChoice = this.selectedPhase.choices[index];
      this.showRemoveChoiceDialog = true;
    },

    /**
     * Conferma la rimozione di una scelta
     */
    confirmRemoveChoice() {
      const phaseIndex = this.pendingRemovePhaseIndex !== null ? this.pendingRemovePhaseIndex : this.selectedPhaseIndex;
      const phase = phaseIndex !== null && this.localConfig.phases[phaseIndex];
      if (!this.pendingRemoveChoice || !phase || !phase.choices) {
        this.pendingRemoveChoice = null;
        this.pendingRemovePhaseIndex = null;
        this.showRemoveChoiceDialog = false;
        return;
      }
      const index = phase.choices.findIndex((choice) => choice.id === this.pendingRemoveChoice.id);
      if (index >= 0 && index < phase.choices.length) {
        phase.choices.splice(index, 1);
        if (this.selectedPhaseIndex === phaseIndex) {
          if (this.selectedChoiceIndex === index) {
            this.selectedChoiceIndex = null;
            this.onElementSelected('phase', phase);
          } else if (this.selectedChoiceIndex > index) {
            this.selectedChoiceIndex--;
          }
        }
        this.onConfigUpdate();
      }
      this.pendingRemoveChoice = null;
      this.pendingRemovePhaseIndex = null;
      this.showRemoveChoiceDialog = false;
    },

    /**
     * Annulla l'aggiunta di una nuova scelta
     */
    cancelAddChoice() {
      this.showAddChoiceDialog = false;
      this.addChoicePhaseIndex = null;
      this.newChoiceTitle = '';
      this.addChoiceFormValid = false;

      if (this.$refs.addChoiceForm) {
        this.$refs.addChoiceForm.reset();
      }
    },

    /**
     * Apre il dialog dettagli prodotto (view-only)
     */
    onProductDetails(productId) {
      const products = this.$store.getters['products/docs'] || [];
      const product = products.find((p) => p.id === productId);
      if (product) {
        this.productDetailsProduct = product;
        this.productDetailsDialog = true;
      }
    },

    /**
     * Genera un ID univoco per le scelte
     */
    generateChoiceId() {
      return `choice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
  },
};
</script>

<style scoped>
/* Stili per i diversi stati del componente */
.component-testeditorproductselection .v-card {
  transition: all 0.3s ease;
}

.component-testeditorproductselection .v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

/* Label step a tutta larghezza (come TestEditorStage) */
.component-testeditorproductselection >>> .v-stepper__label {
  text-align: left !important;
  width: 100% !important;
}

.component-testeditorproductselection >>> .v-stepper--vertical .v-stepper__step {
  padding: 16px 24px;
}

/* Stepper: step fase selezionabile (edit) */
.phase-selectable >>> .v-stepper__step {
  cursor: pointer;
}

.phase-selectable >>> .v-stepper__step:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Evidenziazione della fase selezionata (stesso pattern di TestEditorStage) */
.component-testeditorproductselection >>> .v-stepper__step.phase-selected {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-radius: 4px !important;
  z-index: 2 !important;
  position: relative !important;
}

.component-testeditorproductselection >>> .v-stepper__step.phase-selected .v-stepper__step__step {
  background-color: #1976d2 !important;
  color: white !important;
}

.component-testeditorproductselection >>> .v-stepper__step.phase-selected .v-stepper__label {
  color: #1976d2 !important;
  font-weight: 600 !important;
}

.phase-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.phase-title-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.phase-title-block {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.phase-title {
  font-weight: 500;
}

.phase-subtitle {
  font-size: 0.85em;
  margin-top: 2px;
  line-height: 1.3;
}

.phase-step-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.phase-step-actions .phase-action-btn {
  margin-left: 2px;
}

.phase-step-actions .delete-btn {
  margin-left: 4px;
}

/* Step Nuova Fase */
.add-phase-step {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 2px dashed #4caf50 !important;
  border-radius: 4px !important;
  margin: 8px 0 !important;
  background-color: #f8f9fa !important;
}

.add-phase-step:hover {
  background-color: #e8f5e8 !important;
  border-color: #2e7d32 !important;
}

.add-phase-step >>> .v-stepper__step__step {
  background-color: #4caf50 !important;
  color: white !important;
}

.add-phase-title {
  color: #4caf50 !important;
  font-weight: 500;
}

/* Stili per il drag & drop (scelte) */
.draggable-phases-container {
  min-height: 50px;
}

.sortHandle {
  cursor: move !important;
}

.sortHandle:hover {
  color: #1976d2 !important;
}

/* Stili per l'elemento durante il drag */
.sortablePhase.sortable-ghost {
  opacity: 0.5;
}

.sortablePhase.sortable-chosen {
  background-color: rgba(25, 118, 210, 0.1);
}

/* Stili per fasi nascoste */
.phase-hidden {
  font-style: italic;
  color: #666 !important;
}

.phase-hidden.phase-title {
  color: #999 !important;
}

.phase-hidden >>> .v-stepper__label {
  color: #999 !important;
}

/* Stili per il pulsante di condizioni */
.condition-warning-btn {
  min-width: 24px !important;
  width: 24px !important;
  height: 24px !important;
}

/* Stili per le icone di contenuto */
.content-icon-btn {
  min-width: 24px !important;
  width: 24px !important;
  height: 24px !important;
}

.content-icon {
  font-size: 18px;
}

/* Blocco scelte: griglia pulsanti grandi (come risposte tipo buttons in Stage) */
.choices-buttons-container.draggable-buttons-container {
  display: flex;
  flex-wrap: wrap;
  margin-left: -12px;
  margin-right: -12px;
}

.choices-buttons-container.draggable-buttons-container >>> .v-col {
  padding-left: 12px;
  padding-right: 12px;
}

.choice-wrapper {
  position: relative;
  width: 100%;
}

.choice-selectable {
  position: relative;
  cursor: pointer;
}

.choice-selectable:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.choice-selected {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-radius: 4px !important;
  color: #1976d2 !important;
  font-weight: 600 !important;
}

.choice-selected .v-label {
  color: #1976d2 !important;
  font-weight: 600 !important;
}

/* Card scelta: titolo, procedura, prodotti */
.choice-card {
  min-height: 120px;
  padding: 8px 10px;
}

.choice-card--edit {
  padding-left: 0;
}

.choice-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.choice-card-title {
  font-weight: 600;
  font-size: 0.95rem;
  flex: 1;
  min-width: 0;
}

.choice-star {
  flex-shrink: 0;
}

.choice-procedure-preview {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.35;
  margin-bottom: 8px;
}

.choice-products-list {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}

.choice-product-item {
  padding: 2px 0;
  line-height: 1.3;
}

.choice-card.choice-hidden {
  border: 2px dashed #ccc !important;
  background-color: #f5f5f5 !important;
  opacity: 0.7;
}

.choice-card.choice-hidden .choice-card-title,
.choice-card.choice-hidden .choice-procedure-preview,
.choice-card.choice-hidden .choice-products-list {
  color: #666 !important;
}

/* Layout interno card scelta: strip azioni + contenuto */
.choice-card-inner {
  display: flex;
  align-items: stretch;
  min-height: inherit;
}

.choice-actions-strip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  width: 32px;
  padding: 2px 0;
  margin-right: 8px;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.choice-actions-middle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.choice-card-content {
  flex: 1;
  min-width: 0;
}

.add-choice-btn {
  min-height: 200px;
  border: 2px dashed #4caf50 !important;
  background-color: #f8f9fa !important;
  color: #4caf50 !important;
  transition: all 0.2s ease;
}

.add-choice-btn:hover {
  border-color: #2e7d32 !important;
  background-color: #e8f5e8 !important;
  color: #2e7d32 !important;
}

.sortableChoice {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sortableChoice:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.sortableChoice.sortable-ghost {
  opacity: 0.5;
  transform: rotate(5deg);
}

.sortableChoice.sortable-chosen {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  transform: scale(1.02);
}

/* Preview: card scelta cliccabile */
.choice-preview-clickable {
  cursor: pointer;
}

.choice-preview-clickable:hover .choice-card {
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
}

/* Preview: riepilogo fase completata nell'header */
.phase-selection-summary {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #4caf50;
  font-weight: 400;
  margin-top: 2px;
}

/* Preview: card scelta selezionata */
.choice-card.choice-preview-selected {
  border-color: #4caf50 !important;
  border-width: 2px !important;
  background-color: #e8f5e9 !important;
}
</style>
